<template>
    <PageLoading v-if="isLoading" />
    <div v-else class="main-wrap courses-category-page">
        <h1>{{ $t('general.news') }}</h1>
        <div class="filter-wrap row q-col-gutter-md">
            <q-input
                v-model="startDate"
                class="col-12 col-sm-6 col-md-4 col-lg-2"
                :label="$tc('general.start_date')"
                dense
                outlined
                clearable
                @click="$refs.startDate.show()"
                @input="resetPagination"
            >
                <template v-slot:append>
                    <q-icon name="event" class="cursor-pointer">
                        <q-popup-proxy
                            ref="startDate"
                            transition-show="scale"
                            transition-hide="scale"
                        >
                            <q-date
                                v-model="dateRange.begin"
                                :locale="
                                    locale.definition.calendarPickerLocale
                                        ? locale.definition.calendarPickerLocale
                                        : null
                                "
                                :mask="datePickerMaskFormat"
                                @input="
                                    ;[
                                        () => $refs.startDate.hide(),
                                        resetPagination(),
                                    ]
                                "
                            />
                        </q-popup-proxy>
                    </q-icon>
                </template>
            </q-input>
            <q-input
                v-model="endDate"
                class="col-12 col-sm-6 col-md-4 col-lg-2"
                :label="$tc('general.end_date')"
                dense
                outlined
                clearable
                @click="$refs.endDate.show()"
                @input="resetPagination"
            >
                <template v-slot:append>
                    <q-icon name="event" class="cursor-pointer">
                        <q-popup-proxy
                            ref="endDate"
                            transition-show="scale"
                            transition-hide="scale"
                        >
                            <q-date
                                v-model="dateRange.end"
                                :locale="
                                    locale.definition.calendarPickerLocale
                                        ? locale.definition.calendarPickerLocale
                                        : null
                                "
                                :mask="datePickerMaskFormat"
                                @input="
                                    ;[
                                        () => $refs.endDate.hide(),
                                        resetPagination(),
                                    ]
                                "
                            />
                        </q-popup-proxy>
                    </q-icon>
                </template>
            </q-input>
            <q-select
                v-model="sortBySelected"
                class="col-12 col-sm-6 col-md-4 col-lg-2"
                square
                outlined
                dense
                clearable
                :options="sortBy"
                :label="$t('general.order_by')"
                @input="resetPagination"
            />
            <q-input
                v-model="search"
                class="col-12 col-sm-6 col-md-grow"
                :placeholder="$t('general.search_posts')"
                square
                outlined
                dense
                clearable
                debounce="1000"
                :input-style="{ color: 'grey' }"
                @input="resetPagination"
            >
            </q-input>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3 col-xl-shrink">
                <q-btn
                    class="search-btn"
                    icon="search"
                    color="primary"
                    unelevated
                    dense
                    @click="fetchPosts()"
                ></q-btn>
            </div>
        </div>
        <div v-if="posts.length" class="courses-wrap">
            <NewsThumb
                v-for="(news, index) in posts"
                :key="index"
                :index="index"
                :news="news"
            />
        </div>
        <h5 v-else>{{ $t('general.no_results') }}</h5>
        <div class="pagination-wrap">
            <q-pagination
                v-if="pagination.max != 0"
                v-model="pagination.page"
                text-color="grey-8"
                :max="pagination.max"
                :max-pages="6"
                :direction-links="true"
                :boundary-links="true"
                :boundary-numbers="true"
                @input="fetchPosts"
            ></q-pagination>
        </div>
    </div>
</template>

<script>
import formatDate from '@/helpers/date-time'
import PageLoading from '@/components/PageLoading'
import NewsThumb from '@/components/NewsThumb'
import postsService from '@/services/modules/posts'
import store from '@/store'
import moment from 'moment'

const loadingKeys = {
    fetchingPosts: 'fetching-posts',
}

const publishedPostStatusId = 2

export default {
    name: 'News',

    components: {
        PageLoading,
        NewsThumb,
    },

    data() {
        return {
            posts: [],

            dateRange: {
                begin: null,
                end: null,
            },

            sortBySelected: null,

            search: '',

            pagination: {
                max: 1,
                page: 1,
                rowsPerPage: 10,
                rowsNumber: 0,
            },
        }
    },

    computed: {
        datePickerMaskFormat() {
            return this.locale &&
                this.locale.definition &&
                this.locale.definition.dateFormat
                ? this.locale.definition.dateFormat
                : 'DD/MM/YYYY'
        },

        isLoading() {
            return this.isFetchingPosts
        },

        isFetchingPosts() {
            return !!this.loadings.filter(
                (loading) => loading.key === loadingKeys.fetchingPosts
            ).length
        },

        sortBy() {
            return [
                {
                    label: this.$t('general.more_recents'),
                    column: 'posts.created_at',
                    direction: 'desc',
                },
                {
                    label: this.$t('general.post_name_asc'),
                    column: 'posts.title',
                    direction: 'asc',
                },
                {
                    label: this.$t('general.post_name_desc'),
                    column: 'posts.title',
                    direction: 'desc',
                },
                {
                    label: this.$t('general.oldest'),
                    column: 'posts.created_at',
                    direction: 'asc',
                },
            ]
        },

        locale() {
            return store.getters['locale/currentLocale']
        },

        startDate: {
            get: function () {
                return this.dateRange.begin || null
            },
            set: function (value) {
                if (value === null) {
                    return (this.dateRange.begin = null)
                }
                return this.dateRange.begin || null
            },
        },

        endDate: {
            get: function () {
                return this.dateRange.end || null
            },
            set: function (value) {
                if (value === null) {
                    return (this.dateRange.end = null)
                }
                return this.dateRange.end || null
            },
        },

        searchQuery() {
            return this.$route.query.search
        },
    },

    watch: {
        search(value) {
            this.search = value
            this.fetchPosts()
        },

        $route(value) {
            this.search = value.query.search
        },

        locale(newValue, oldValue) {
            this.convertDates(newValue, oldValue)
        },
    },

    mounted() {
        this.search = this.searchQuery ? this.searchQuery : this.search
    },

    async created() {
        await this.fetchPosts()
    },

    methods: {
        convertDates(newLocale, oldLocale) {
            this.dateRange.begin = this.dateRange.begin
                ? moment(
                      this.dateRange.begin,
                      oldLocale.definition.dateFormat
                  ).format(newLocale.definition.dateFormat)
                : this.dateRange.begin
            this.dateRange.end = this.dateRange.end
                ? moment(
                      this.dateRange.end,
                      oldLocale.definition.dateFormat
                  ).format(newLocale.definition.dateFormat)
                : this.dateRange.end
        },

        resetPagination() {
            this.pagination.page = 1
            this.fetchPosts()
        },

        constructOrderBy() {
            let orderBy = this.sortBySelected
                ? {
                      column: this.sortBySelected.column,
                      direction: this.sortBySelected.direction,
                  }
                : { column: 'start_date', direction: 'desc' }
            return orderBy
        },

        constructPagination() {
            return {
                perPage: this.pagination.rowsPerPage,
                currentPage: this.pagination.page,
            }
        },

        async fetchPosts() {
            this.loadingAdd(loadingKeys.fetchingPosts)

            let parameters = {
                filter: {
                    search: this.search,
                    enabled: true,
                    status: publishedPostStatusId,
                    isBetweenVisibleDates: true,
                    startDate: this.startDate
                        ? moment(this.startDate, this.datePickerMaskFormat)
                              .set({ hour: 0, minute: 0, second: 0 })
                              .format('YYYY-MM-DD HH:mm:ss')
                        : null,
                    endDate: this.endDate
                        ? moment(this.endDate, this.datePickerMaskFormat)
                              .set({ hour: 23, minute: 59, second: 59 })
                              .format('YYYY-MM-DD HH:mm:ss')
                        : null,
                },
                orderBy: [this.constructOrderBy()],
                pagination: this.constructPagination(),
            }

            let response = await postsService.retrieve(parameters)

            if (response.status === 200) {
                this.posts = response.data.results

                let total = response.data.pagination.total
                let pages = total / this.pagination.rowsPerPage
                this.pagination.max = Math.ceil(pages)
            }

            this.loadingRemove(loadingKeys.fetchingPosts)
        },
    },
}
</script>
